import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private currentLangSubject = new BehaviorSubject<string>('ar');
  public currentLang$ = this.currentLangSubject.asObservable();

  constructor(private translate: TranslateService) {
    const savedLang = localStorage.getItem('lang');
    if (savedLang && ['ar', 'fr', 'en'].includes(savedLang)) {
      this.currentLangSubject.next(savedLang);
      this.translate.setDefaultLang(savedLang);
      this.translate.use(savedLang).subscribe({
        next: () => {
          console.log(`Successfully loaded translations for language: ${savedLang}`);
          document.documentElement.setAttribute('dir', savedLang === 'ar' ? 'rtl' : 'ltr');
        },
        error: (error) => {
          console.error(`Failed to load translations for language: ${savedLang}`, error);
          this.fallbackToDefault();
        }
      });
    } else {
      this.fallbackToDefault();
    }
  }

  private fallbackToDefault() {
    this.translate.setDefaultLang('ar');
    this.translate.use('ar').subscribe({
      next: () => {
        console.log('Successfully loaded default language (ar)');
        document.documentElement.setAttribute('dir', 'rtl');
      },
      error: (error) => {
        console.error('Failed to load default language (ar)', error);
      }
    });
    this.currentLangSubject.next('ar');
    localStorage.setItem('lang', 'ar');
  }

  setLanguage(lang: string): void {
    if (!['ar', 'fr', 'en'].includes(lang)) {
      lang = 'ar';
    }

    if (lang === this.currentLangSubject.value) {
      return;
    }

    this.currentLangSubject.next(lang);

    this.translate.use(lang).subscribe({
      next: () => {
        console.log(`Successfully switched to language: ${lang}`);
        localStorage.setItem('lang', lang);
        document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
      },
      error: (error) => {
        console.error(`Failed to load translations for language: ${lang}`, error);
        this.fallbackToDefault();
      }
    });
  }

  getCurrentLang(): string {
    return this.currentLangSubject.value;
  }

  getDirection(): string {
    return this.currentLangSubject.value === 'ar' ? 'rtl' : 'ltr';
  }

  toggleLanguage(): void {
    const current = this.getCurrentLang();
    let nextLang: string;

    switch (current) {
      case 'ar':
        nextLang = 'en';
        break;
      case 'en':
        nextLang = 'fr';
        break;
      default:
        nextLang = 'ar';
        break;
    }

    this.setLanguage(nextLang);
  }
}